import React, { useEffect, useState } from 'react';
import { Modal, UnstyledButton, Loader } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { PrinterView } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { printersService } from '../../../../services/printers/printers.service';
import classes from './fixed-printer-modal.module.scss';

export type PinnedBonPrinterModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
  pinnedBonPrinterId: number | null;
  onSelect: (printerId: number) => void;
  onClear: () => void;
};

// Summary: Manager picker choosing a printer whose bons this device fires without printing.
const PinnedBonPrinterModalComponent: React.FC<PinnedBonPrinterModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  pinnedBonPrinterId,
  onSelect,
  onClear,
}) => {
  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(pinnedBonPrinterId);

  useEffect(() => {
    if (!opened || !employeeSession) return;
    setSelectedId(pinnedBonPrinterId);
    setLoading(true);
    printersService
      .listActive(tenantId, employeeSession)
      .then((data) => setPrinters(data))
      .catch(() => setPrinters([]))
      .finally(() => setLoading(false));
  }, [opened, tenantId, employeeSession, pinnedBonPrinterId]);

  const handleSave = () => {
    if (selectedId == null) return;
    onSelect(selectedId);
    onClose();
  };

  const handleClear = () => {
    onClear();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onContextMenu={(event) => event.preventDefault()}
      title={<strong>קיבוע בונים למכשיר</strong>}
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: navyModal.root }}
      zIndex={400}
      closeButtonProps={{ 'data-testid': 'pinned-bon-printer-close' } as Record<string, string>}
    >
      <div className={classes.stack}>
        <span className={classes.empty}>הזמנות למדפסת שנבחרה יישלחו למטבח ללא הדפסה מהמכשיר הזה.</span>
        {loading ? (
          <div className={classes.center}><Loader /></div>
        ) : printers.length === 0 ? (
          <span className={classes.empty}>אין מדפסת פעילה</span>
        ) : (
          <div className={classes.list}>
            {printers.map((printer) => {
              const isSelected = selectedId === printer.id;
              return (
                <UnstyledButton
                  key={printer.id}
                  component="button"
                  type="button"
                  data-testid={`pinned-bon-printer-option-${printer.id}`}
                  className={isSelected ? `${classes.item} ${classes.itemActive}` : classes.item}
                  onClick={() => setSelectedId(printer.id)}
                >
                  <span>{printer.name}</span>
                  {isSelected && <FontAwesomeIcon icon={faCheck as IconProp} />}
                </UnstyledButton>
              );
            })}
          </div>
        )}
        <div className={classes.actions}>
          <Button variant="cancel" onClick={handleClear} disabled={pinnedBonPrinterId == null} dataTestId="pinned-bon-printer-clear">
            נקה / בטל קיבוע
          </Button>
          <Button variant="save" onClick={handleSave} disabled={selectedId == null} dataTestId="pinned-bon-printer-save">
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const PinnedBonPrinterModal = React.memo(PinnedBonPrinterModalComponent);
